import { PDFDocument } from 'pdf-lib';
import { Template } from '@pdfme/generator';
import { generatePdfUint8Array } from './pdfUtilities';
import { getFont, toPdfStr, splitConstructSub } from '../utils';
import { SITE_CONTACT_ITEM_PER_PAGE } from '../const/Const';

export const siteContactPdf = async (data: any) => {
  // 初回読み込みの容量を減らすため動的インポート
  const basePdf1 = await import('../assets/site_contact_base_pdf1');
  const basePdf2 = await import('../assets/site_contact_base_pdf2');
  const font = await getFont();

  // 項目数から、必要なページ数を計算
  const remainingItems =
    data.items.length - SITE_CONTACT_ITEM_PER_PAGE['FIRST_PAGE'];
  const totalPages =
    Math.ceil(
      remainingItems / SITE_CONTACT_ITEM_PER_PAGE['SECOND_PAGE_AND_AFTER']
    ) + 1;

  const pdfDoc = await PDFDocument.create(); // Create a new PDF document

  // ページの最初の項目と最後の項目のインデックス
  let startIdx = 0;
  let endIdx = Math.min(
    data.items.length,
    SITE_CONTACT_ITEM_PER_PAGE['FIRST_PAGE']
  );

  // 必要ページ数分、ページを生成する
  for (let pageIndex = 1; pageIndex <= totalPages; pageIndex++) {
    // ページを生成するためのプロパティ
    let schemas = {},
      inputs = {},
      offsetY = 20.5,
      basePdf = basePdf2.basePdf;

    /**
     * 1ページ目のみ、現場基本情報のスキーマと入力データを作成し、各工事項目が始まるy方向の位置を
     * 調整する. また、basePdfを変更する
     */
    if (pageIndex === 1) {
      schemas = createSiteInfoSchemas();
      inputs = createSiteInfoInputs(data);
      offsetY = 168;
      basePdf = basePdf1.basePdf;
    }

    // ページを生成
    const page = await generatePage(
      data,
      font,
      startIdx,
      endIdx,
      offsetY,
      schemas,
      inputs,
      basePdf
    );

    const pdf = await PDFDocument.load(page);
    const copiedPages = await pdfDoc.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach(copiedPage => pdfDoc.addPage(copiedPage));

    // ページの最初の項目と最後の項目のインデックスを更新
    startIdx = endIdx;
    endIdx = Math.min(
      data.items.length,
      endIdx + SITE_CONTACT_ITEM_PER_PAGE['SECOND_PAGE_AND_AFTER']
    );
  }

  const exportPdf = await pdfDoc.save();

  return new Blob([exportPdf.buffer], { type: 'application/pdf' });
};

const generatePage = async (
  data: any,
  font: any,
  startIdx: number,
  endIdx: number,
  offsetY: number,
  schemas: any,
  inputs: any,
  basePdf: any
) => {
  // 開始から終了までの範囲の項目に対して処理を行う
  for (let idx = startIdx; idx < endIdx; idx++) {
    const item = data.items[idx];
    // ページ内でのインデックス
    let idxInPage = idx - startIdx;

    // 各工事内容のスキーマと入力データを作成
    addItemRow(
      schemas,
      inputs,
      item,
      idxInPage,
      idx,
      offsetY + idxInPage * 7.05
    );
  }

  // テンプレートにスキーマを入れる
  const template: Template = {
    basePdf: basePdf,
    schemas: [schemas]
  };

  // テンプレートに入力データを入れてPDFを生成
  return await generatePdfUint8Array(template, [inputs], font);
};

/**
 * 各工事項目を表すスキーマと入力データを、
 * PDF出力のためのスキーマオブジェクトと入力データオブジェクトに追加する.
 *
 * @param schemas PDF出力のためのスキーマオブジェクト
 * @param inputs PDF出力のための入力データオブジェクト
 * @param item 工事項目
 * @param indexInPage ページ内でのインデックス
 * @param itemIndex 全工事項目の中でのインデックス
 * @param yPos y方向開始位置
 */
const addItemRow = (
  schemas: any,
  inputs: any,
  item: any,
  indexInPage: number,
  itemIndex: number,
  yPos: number
) => {
  // 共通のスキーマ
  const commonSchema = {
    type: 'text',
    height: 6.6,
    verticalAlignment: 'middle'
  };

  // 通番のスキーマ
  schemas[`serial_number${indexInPage}`] = {
    position: {
      x: 7.2,
      y: yPos
    },
    width: 17.27,
    alignment: 'center',
    fontSize: 10,
    ...commonSchema
  };
  // 工事内容のスキーマ
  schemas[`construction_name${indexInPage}`] = {
    position: {
      x: 23,
      y: yPos
    },
    width: 55,
    dynamicFontSize: {
      max: 9,
      min: 6
    },
    alignment: 'left',
    ...commonSchema
  };
  // 仕様のスキーマ
  schemas[`spec${indexInPage}`] = {
    position: {
      x: 79,
      y: yPos
    },
    width: 55,
    dynamicFontSize: {
      max: 9,
      min: 6
    },
    alignment: 'left',
    ...commonSchema
  };
  // 数量のスキーマ
  schemas[`quantity${indexInPage}`] = {
    position: {
      x: 131.5,
      y: yPos
    },
    width: 17.5,
    dynamicFontSize: {
      max: 9,
      min: 6
    },
    alignment: 'right',
    ...commonSchema
  };
  // 備考のスキーマ
  schemas[`remarks${indexInPage}`] = {
    position: {
      x: 150,
      y: yPos
    },
    width: 49,
    dynamicFontSize: {
      max: 9,
      min: 6
    },
    alignment: 'left',
    ...commonSchema
  };

  // 通番の入力データ
  inputs[`serial_number${indexInPage}`] = String(itemIndex + 1);
  // 工事内容の入力データ
  inputs[`construction_name${indexInPage}`] = item.construction_name
    ? item.construction_name
    : '';
  // 仕様の入力データ
  inputs[`spec${indexInPage}`] = item.spec ? item.spec : '';
  // 数量の入力データ
  inputs[`quantity${indexInPage}`] = item.quantity
    ? item.quantity.toLocaleString('ja-JP') + ' ' + item.unit_name + ' '
    : '0 ' + item.unit_name + ' ';
  // 備考の入力データ
  inputs[`remarks${indexInPage}`] = item.remarks ? item.remarks : '';
};

/**
 * 現場基本情報のスキーマを作成する
 *
 * @returns 現場基本情報のスキーマ
 */
const createSiteInfoSchemas = () => {
  return {
    submission_date: {
      // 提出日
      type: 'text',
      position: {
        x: 149,
        y: 9.1
      },
      width: 35,
      height: 5,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    foreman: {
      // 職長
      type: 'text',
      position: {
        x: 18,
        y: 15.8
      },
      width: 120.5,
      height: 9.38,
      alignment: 'center',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    site_contact_remarks: {
      // 備考
      type: 'text',
      position: {
        x: 11.5,
        y: 30.23
      },
      width: 119.5,
      height: 19.76,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1
    },
    construction_number: {
      // 工事番号
      type: 'text',
      position: {
        x: 165.3,
        y: 73.05
      },
      width: 860,
      height: 10,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    construction_site_name: {
      // 現場名
      type: 'text',
      position: {
        x: 40,
        y: 73.05
      },
      width: 660,
      height: 10,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    construction_site_name_first_line: {
      // 現場名1行目
      type: 'text',
      position: {
        x: 40,
        y: 72.5
      },
      width: 96,
      height: 10,
      alignment: 'left',
      fontSize: 8,
      characterSpacing: 0,
      lineHeight: 0
    },
    construction_site_name_second_line: {
      // 現場名2行目
      type: 'text',
      position: {
        x: 40,
        y: 75.5
      },
      width: 96,
      height: 10,
      alignment: 'left',
      fontSize: 8,
      characterSpacing: 0,
      lineHeight: 0
    },
    site_address: {
      // 現場住所
      type: 'text',
      position: {
        x: 40,
        y: 80
      },
      width: 170,
      height: 70.25,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    construct_date: {
      // 工事日
      type: 'text',
      position: {
        x: 40,
        y: 87
      },
      width: 66,
      height: 70.25,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    meetup_time: {
      // 集合時間
      type: 'text',
      position: {
        x: 130,
        y: 87
      },
      width: 86,
      height: 70.25,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    work_company: {
      // 施工会社
      type: 'text',
      position: {
        x: 40,
        y: 99
      },
      width: 161.5,
      height: 9,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    site_supervizor: {
      // 現場担当者
      type: 'text',
      position: {
        x: 40,
        y: 106
      },
      width: 161.5,
      height: 9,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    site_supervizor_tel: {
      // 現場担当者TEL
      type: 'text',
      position: {
        x: 40,
        y: 112.5
      },
      width: 161.5,
      height: 9,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1
    },
    contact_note: {
      // 連絡事項
      type: 'text',
      position: {
        x: 11.5,
        y: 135.5
      },
      width: 185,
      height: 40,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1
    },
    company_name: {
      // 会社名
      type: 'text',
      position: {
        x: 139,
        y: 13.4
      },
      width: 63,
      height: 5.97,
      alignment: 'left',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 1.5
    },
    company_name_long: {
      // 会社名の長さで、フィールドの位置を切り替える
      type: 'text',
      position: {
        x: 139,
        y: 12.4
      },
      width: 63,
      height: 5,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1.2
    },
    company_info: {
      // 会社住所
      type: 'text',
      position: {
        x: 139,
        y: 21
      },
      width: 66,
      height: 34.81,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1
    },
    user_info: {
      // 担当者
      type: 'text',
      position: {
        x: 139,
        y: 50
      },
      width: 63,
      height: 34.81,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1
    }
  };
};

/**
 * 現場情報のを作成する
 * @param data 現場情報
 * @returns 現場情報の入力データ
 */
const createSiteInfoInputs = (data: any) => {
  let long_company_name_flg = data.company_name.length > 14;

  const input: any = {
    // 会社印
    companyStamp:
      data.company_stamp && data.company_stamp !== ''
        ? `data:${data.company_stamp_format};base64,${data.company_stamp}`
        : '',
    // 職長
    foreman: data.foreman ? data.foreman : ' ',
    // 備考
    site_contact_remarks: data.site_contact_remarks
      ? data.site_contact_remarks
      : ' ',
    // 工事番号
    construction_number: String(data.construction_number),
    // 現場住所
    site_address: data.site_address ? data.site_address : ' ',
    // 工事日
    construct_date: window.location.pathname.includes('/received')
      ? data.construct_date
      : data.construct_date_str,
    // 集合時間
    meetup_time: data.meetup_time ? data.meetup_time : ' ',
    // 施工会社
    work_company: data.work_company
      ? data.work_company.replace(/[\r\n]+/g, ' ')
      : ' ',
    // 現場担当者
    site_supervizor: data.site_supervizor ? data.site_supervizor : '',
    // 現場担当者TEL
    site_supervizor_tel: data.site_supervizor_tel
      ? data.site_supervizor_tel
      : ' ',
    // 連絡事項
    contact_note: data.contact_note ? data.contact_note : ' ',
    // 提出日
    submission_date: window.location.pathname.includes('/received')
      ? data.submission_date
      : data.submission_date_str,
    // 会社名
    company_name: long_company_name_flg ? '' : data.company_name,
    // 会社名の長さで、フィールドの位置を切り替える
    company_name_long: long_company_name_flg ? data.company_name : '',
    // 会社住所
    company_info: `〒${data.company_postal_code.slice(
      0,
      3
    )} - ${data.company_postal_code.slice(-4)}
${data.company_address}

TEL: ${data.company_phone_number}
FAX: ${data.company_fax_number}
`,
    user_info: `
担当者: ${data.user_name}
担当者TEL: ${data.user_tel}
`
  };

  if (data.construction_name.length < 34) {
    input['construction_site_name'] = data.construction_name
      ? data.construction_name.replace(/\r\n|\r|\n/g, ' ')
      : ' ';
  }
  // 現場名が34文字以上のときは複数行にする
  else {
    const constructSub = data.construction_name.replace(/\r\n|\r|\n/g, ' ');
    const { firstLine, secondLine } = splitConstructSub(constructSub, 34); // ここで改行コードの補正はしている
    input['construction_site_name_first_line'] = toPdfStr(firstLine);
    input['construction_site_name_second_line'] = toPdfStr(secondLine);
  }

  return input;
};
